import { useState } from 'react';
import { X, Wand2, Loader, AlertCircle, CheckCircle, Brain } from 'lucide-react';
import { FormSection } from '../../types/formBuilder';
import { createNewSection, createNewField } from '../../utils/formBuilderHelpers';
import { FunctionsHttpError } from '@supabase/functions-js';
import { getPublicFalQueueWebhookFunctionUrl } from '../../utils/falWebhookUrl';
import { parseAdminStaffSession } from '../../utils/adminSession';
import { supabase } from '../../utils/supabaseStorage';

interface JsonAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateForm: (sections: FormSection[], modelConfig: any) => void;
}

type FalClientMode = 'stream' | 'queue';

interface AnalysisResult {
  modelName: string;
  description: string;
  workflowPath: string;
  outputKey: string;
  iconUrl?: string;
  /** Stream vs Queue — drives model fal_client_mode */
  falClientMode: FalClientMode;
  /** Optional per-model queue webhook override; empty = use project default */
  webhookUrl: string;
  sections: FormSection[];
}

export const JsonAIModal = ({ isOpen, onClose, onGenerateForm }: JsonAIModalProps) => {
  const [apiExample, setApiExample] = useState('');
  const [falClientMode, setFalClientMode] = useState<FalClientMode>('stream');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const projectWebhookBase = getPublicFalQueueWebhookFunctionUrl();

  const analyzeWithOpenAI = async (apiText: string, mode: FalClientMode): Promise<AnalysisResult> => {
    // Ensure a real user JWT (not anon key as Bearer) — supabase-js uses session for Edge Functions; without it, gateway returns 401.
    const { data: udata, error: uerr } = await supabase.auth.getUser();
    if (uerr || !udata.user) {
      const staff = parseAdminStaffSession();
      if (staff) {
        throw new Error(
          'Your sign-in to the server has expired, but a staff session is still stored. ' +
            'Use Sign out in the admin bar, then sign in again at /admin, and retry JSON AI.'
        );
      }
      throw new Error('Sign in required. Open /admin and sign in with a staff (admin) account, then try again.');
    }

    const invoke = () =>
      supabase.functions.invoke<{ success?: boolean; content?: string; error?: string }>('json-ai-model', {
        body: { apiText, falClientMode: mode },
      });

    let { data: payload, error: invokeError } = await invoke();
    if (
      invokeError instanceof FunctionsHttpError &&
      (invokeError.context as Response | undefined)?.status === 401
    ) {
      await supabase.auth.refreshSession();
      ({ data: payload, error: invokeError } = await invoke());
    }
    if (invokeError) {
      let message = invokeError.message;
      if (invokeError instanceof FunctionsHttpError && invokeError.context) {
        try {
          const b = (await (invokeError.context as Response).json()) as {
            error?: string;
            message?: string;
            msg?: string;
          };
          if (typeof b.error === 'string') message = b.error;
          else if (typeof b.message === 'string') message = b.message;
          else if (typeof b.msg === 'string') message = b.msg;
        } catch {
          if ((invokeError.context as Response).status === 401) {
            message =
              'Unauthorized (401). Your session may be expired—sign out and sign in at /admin again, then retry.';
          }
        }
      }
      throw new Error(message);
    }
    if (!payload || !(payload as { success?: boolean }).success) {
      const p = payload as { error?: string } | null;
      if (p && typeof p.error === 'string') throw new Error(p.error);
      throw new Error('Analysis failed');
    }
    const content = (payload as { content?: string }).content;

    if (!content) {
      throw new Error('No content in analysis response. Try again or check Edge Function logs.');
    }

    try {
      // Extract JSON from response (in case there's extra text)
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      const jsonString = jsonMatch ? jsonMatch[0] : content;
      const parsedResult = JSON.parse(jsonString);

      // Convert to our format
      const section = createNewSection(0);
      section.title = 'Model Parameters';
      section.fields = parsedResult.fields.map((fieldData: any) => ({
        ...createNewField(),
        label: fieldData.label,
        variable_name: fieldData.variable_name,
        type: fieldData.type,
        required: fieldData.required || false,
        placeholder: fieldData.placeholder,
        description: fieldData.description,
        advanced: fieldData.advanced || false,
        defaultValue: fieldData.defaultValue,
        min: fieldData.min,
        max: fieldData.max,
        step: fieldData.step,
        options: fieldData.options
      }));

      const parsedMode: FalClientMode =
        parsedResult.falClientMode === 'queue' || parsedResult.falClientMode === 'stream'
          ? parsedResult.falClientMode
          : mode;
      const wh =
        typeof parsedResult.webhookUrl === 'string' ? parsedResult.webhookUrl : '';

      return {
        modelName: parsedResult.modelName,
        description: parsedResult.description,
        workflowPath: parsedResult.workflowPath,
        outputKey: parsedResult.outputKey,
        iconUrl: parsedResult.iconUrl,
        falClientMode: parsedMode,
        webhookUrl: wh,
        sections: [section]
      };
    } catch (parseError) {
      console.error('JSON parsing error:', parseError);
      console.log('OpenAI response:', content);
      throw new Error('Failed to parse OpenAI response. Please try again with a clearer API example.');
    }
  };

  const handleAnalyze = async () => {
    if (!apiExample.trim()) {
      setError('Please paste an API example first');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const looksQueue = /fal\.queue|queue\.submit/i.test(apiExample);
      const effectiveMode: FalClientMode = looksQueue ? 'queue' : falClientMode;
      if (looksQueue && falClientMode !== 'queue') {
        setFalClientMode('queue');
      }
      const analysisResult = await analyzeWithOpenAI(apiExample, effectiveMode);
      setResult(analysisResult);
    } catch (error) {
      console.error('Analysis error:', error);
      setError(error instanceof Error ? error.message : 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApplyResult = () => {
    if (result) {
      onGenerateForm(result.sections, {
        modelName: result.modelName,
        description: result.description,
        category: 'models',
        tags: '',
        workflowPath: result.workflowPath,
        outputKey: result.outputKey,
        iconUrl: result.iconUrl,
        falClientMode: result.falClientMode,
        webhookUrl: result.webhookUrl
      });
      onClose();
      // Clear state
      setApiExample('');
      setResult(null);
      setError(null);
    }
  };

  const handleClose = () => {
    setApiExample('');
    setFalClientMode('stream');
    setResult(null);
    setError(null);
    setIsAnalyzing(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-base/90 z-modal-content flex items-center justify-center p-4">
      <div className="bg-surface border border-subtle rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-subtle">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-status-warning rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            {result && (
              <div className="bg-status-warning/10 border border-status-warning/20 rounded p-2">
                <p className="text-status-warning text-xs font-medium">📍 Complete Output Path</p>
                <code className="text-status-warning font-mono text-xs block mt-1">
                  {`result.output.${result.outputKey}[0].url`}
                </code>
                <p className="text-th-secondary text-xs mt-1">
                  JavaScript dot notation for this project
                </p>
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-th-primary">JSON AI Assistant</h2>
              <p className="text-sm text-th-secondary">
                Auto-generate form fields from API examples using AI
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-elevated border border-subtle rounded-lg p-4">
            <h3 className="text-th-primary font-semibold mb-2">📋 How to Use</h3>
            <ol className="text-th-secondary text-sm space-y-1 list-decimal list-inside">
              <li>Paste your FAL API documentation or example code below</li>
              <li>Include both input parameters and expected output format</li>
              <li>Click "Analyze with AI" to auto-generate form fields</li>
              <li>Review the generated configuration and apply it to your form</li>
            </ol>
          </div>

          <div className="space-y-2">
            <span className="block text-sm font-medium text-th-primary">FAL API</span>
            <div className="inline-flex rounded-lg border border-subtle p-0.5 bg-elevated">
              <button
                type="button"
                onClick={() => setFalClientMode('stream')}
                disabled={isAnalyzing}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  falClientMode === 'stream'
                    ? 'bg-brand text-surface'
                    : 'text-th-secondary hover:text-th-primary'
                }`}
              >
                Stream
              </button>
              <button
                type="button"
                onClick={() => setFalClientMode('queue')}
                disabled={isAnalyzing}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  falClientMode === 'queue'
                    ? 'bg-brand text-surface'
                    : 'text-th-secondary hover:text-th-primary'
                }`}
              >
                Queue
              </button>
            </div>
            <p className="text-xs text-th-secondary">
              {falClientMode === 'stream'
                ? 'Uses fal.stream — stream output events to completion.'
                : `Uses fal.queue.submit — set optional webhook in model settings, or use project URL: ${projectWebhookBase || '(set VITE_SUPABASE_URL)'}?token=…`}{' '}
            </p>
          </div>

          {/* API Example Input */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-th-primary">
              Paste API Example or Documentation
            </label>
            <textarea
              value={apiExample}
              onChange={(e) => setApiExample(e.target.value)}
              placeholder={`Example:
const stream = await fal.stream("workflows/sanjuniperAI/text-to-video", {
  input: {
    input_concept: "A beautiful sunset over mountains",
    style: "cinematic",
    camera_style: "static",
    camera_direction: "forward",
    pacing: "slow",
    special_effects: "none",
    image_url: "",
    aspect_ratio: "16:9"
  }
});

// Output:
{
  "video": {
    "url": "https://...",
    "file_name": "output.mp4"
  }
}`}
              rows={12}
              className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-field placeholder-th-tertiary focus:border-brand focus:outline-none resize-none font-mono text-sm"
              disabled={isAnalyzing}
            />
            <p className="text-xs text-th-secondary">
              Include workflow path, input parameters, and expected output format for best results
            </p>
          </div>

          {/* Analysis Result */}
          {result && (
            <div className="bg-overlay border border-subtle rounded-lg p-4 space-y-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-th-secondary" />
                <h3 className="text-th-primary font-semibold">Analysis Complete</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-th-secondary">Model Name:</span>
                  <span className="text-th-primary font-medium ml-2">{result.modelName}</span>
                </div>
                <div>
                  <span className="text-th-secondary">Workflow Path:</span>
                  <span className="text-th-primary font-mono text-xs ml-2">{result.workflowPath}</span>
                </div>
                <div>
                  <span className="text-th-secondary">Output Key:</span>
                  <span className="text-th-primary font-mono text-xs ml-2">result.output.{result.outputKey}[0].url</span>
                </div>
                <div>
                  <span className="text-th-secondary">FAL client:</span>
                  <span className="text-th-primary font-medium ml-2">
                    {result.falClientMode === 'queue' ? 'Queue' : 'Stream'}
                  </span>
                </div>
                {result.falClientMode === 'queue' && (
                  <div className="md:col-span-2">
                    <span className="text-th-secondary">Webhook override:</span>
                    <span className="text-th-primary font-mono text-xs ml-2 break-all">
                      {result.webhookUrl?.trim() ? result.webhookUrl : '— (project default)'}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-th-secondary">Fields Generated:</span>
                  <span className="text-th-secondary font-medium ml-2">
                    {result.sections.reduce((total, section) => total + section.fields.length, 0)} fields
                  </span>
                </div>
              </div>
              
              <div className="pt-2 border-t border-subtle/50">
                <span className="text-th-secondary text-sm">Description:</span>
                <p className="text-th-primary text-sm mt-1 leading-relaxed">{result.description}</p>
              </div>

              {/* Generated Fields Preview */}
              <div className="pt-2 border-t border-subtle/50">
                <h4 className="text-th-secondary text-sm mb-2">Generated Fields:</h4>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {result.sections.flatMap(section => section.fields).map((field, index) => (
                    <div key={index} className="flex items-center justify-between bg-elevated/50 rounded px-3 py-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-th-primary text-sm font-medium">{field.label}</span>
                        <span className="text-th-secondary text-xs font-mono">({field.variable_name})</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-th-secondary text-xs px-2 py-0.5 bg-muted/40 rounded">
                          {field.type}
                        </span>
                        {field.required && (
                          <span className="text-status-error text-xs">*</span>
                        )}
                        {field.advanced && (
                          <span className="text-status-warning text-xs px-1.5 py-0.5 bg-status-warning/20 rounded">
                            ADV
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-overlay border border-subtle rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-status-error" />
                <span className="text-status-error font-medium">Analysis Failed</span>
              </div>
              <p className="text-status-error/70 text-sm mt-2">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-subtle">
          <div className="text-sm text-th-secondary">
            {result ? 'Review the generated configuration and apply it to your form' : 'Paste your API example above to get started'}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              disabled={isAnalyzing}
              className="px-4 py-2 bg-elevated border border-subtle text-th-secondary rounded-lg font-medium hover:text-th-primary hover:border-brand/50 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            
            {!result ? (
              <button
                onClick={handleAnalyze}
                disabled={!apiExample.trim() || isAnalyzing}
                className="flex items-center space-x-2 px-6 py-2 bg-status-warning text-white rounded-lg font-medium hover:bg-status-warning/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isAnalyzing ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4" />
                )}
                <span>{isAnalyzing ? 'Analyzing...' : 'Analyze with AI'}</span>
              </button>
            ) : (
              <button
                onClick={handleApplyResult}
                className="flex items-center space-x-2 px-6 py-2 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 transition-all"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Apply Configuration</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};